import { useAuthStore } from "@/shared/lib/auth-store";

/** Тело ошибки, которое отдаёт бэкенд (см. app/core/errors.py). */
export interface ApiErrorBody {
  error: { code: string; message: string; details?: Record<string, unknown> };
}

export class ApiError extends Error {
  status: number;
  code: string;
  details: Record<string, unknown>;

  constructor(
    status: number,
    code: string,
    message: string,
    details?: Record<string, unknown>,
  ) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.details = details ?? {};
  }
}

/**
 * Ходит на наш FastAPI:
 * - проставляет Authorization из Zustand,
 * - сериализует body через JSON,
 * - на 401 — logout + редирект на /login (без хуков, через window.location).
 * - парсит {error:{code,message,details}} → ApiError.
 *
 * `path` начинается с `/api/...`, потому что Vite-dev и прод-сервер
 * монтируют наш бэкенд по этому префиксу.
 */
export async function api<T = unknown>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const token = useAuthStore.getState().token;

  const headers = new Headers(init.headers);
  if (token) headers.set("Authorization", `Bearer ${token}`);
  // Для FormData Content-Type выставляет fetch сам (с boundary).
  const isFormData =
    typeof FormData !== "undefined" && init.body instanceof FormData;
  if (init.body && !isFormData && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  headers.set("Accept", "application/json");

  let response: Response;
  try {
    response = await fetch(path, { ...init, headers });
  } catch (e) {
    throw new ApiError(0, "NETWORK_ERROR", "Сеть недоступна, попробуйте позже", {
      cause: String(e),
    });
  }

  if (response.status === 401) {
    useAuthStore.getState().logout();
    if (typeof window !== "undefined" && !window.location.pathname.startsWith("/login")) {
      window.location.assign("/login");
    }
  }

  if (response.status === 204) {
    return undefined as T;
  }

  const contentType = response.headers.get("content-type") ?? "";
  const isJson = contentType.includes("application/json");

  if (!response.ok) {
    if (isJson) {
      try {
        const body = (await response.json()) as Partial<ApiErrorBody>;
        const err = body.error;
        if (err && typeof err === "object" && err.code && err.message) {
          throw new ApiError(response.status, err.code, err.message, err.details);
        }
      } catch (e) {
        if (e instanceof ApiError) throw e;
        // не-валидный JSON — провалимся в общий бранч.
      }
    }
    const text = await response.text().catch(() => "");
    throw new ApiError(
      response.status,
      "HTTP_ERROR",
      text.slice(0, 200) || `HTTP ${response.status}`,
    );
  }

  if (!isJson) {
    return undefined as T;
  }
  return (await response.json()) as T;
}

export const apiPost = <T = unknown>(path: string, body: unknown) =>
  api<T>(path, { method: "POST", body: JSON.stringify(body) });

export const apiPut = <T = unknown>(path: string, body: unknown) =>
  api<T>(path, { method: "PUT", body: JSON.stringify(body) });

export const apiDelete = <T = unknown>(path: string) =>
  api<T>(path, { method: "DELETE" });

/** Загрузить файл (multipart/form-data). Не выставляем Content-Type —
 *  fetch сам добавит его с boundary. */
export const apiUpload = <T = unknown>(
  path: string,
  file: File,
  field = "file",
) => {
  const fd = new FormData();
  fd.append(field, file);
  return api<T>(path, { method: "POST", body: fd });
};
